/// <amd-module name="Wiki/PageComments/WikiPageCommentsClient" />
import {
    Comment,
    CommentAttachment,
    CommentCreateParameters,
    CommentExpandOptions,
    CommentReaction,
    CommentReactionType,
    CommentUpdateParameters,
    ICommentArtifactInfo,
    ICommentPage,
    ICommentsClient
} from "Comments/Common/Contracts";
import { IVssPageContext } from "VSS/Platform/Context";
import { IdentityRef } from "VSS/Platform/Contracts/WebApi";
import { getWikiClient, IWikiRestClient } from "Wiki/Clients/Wiki/RestClient/Wiki";
import { convertArtifactInfoToWikiCommentArtifact } from "Wiki/PageComments/WikiPageCommentsHelper";

export class WikiPageCommentsClient implements ICommentsClient {
    private _wikiRestClient: IWikiRestClient;

    constructor(pageContext: IVssPageContext) {
        this._wikiRestClient = getWikiClient(pageContext);
    }

    public addComment(artifactInfo: ICommentArtifactInfo, value: string, parentId?: number): Promise<Comment> {
        const request = { text: value, parentId } as CommentCreateParameters;
        const wikiCommentArtifact = convertArtifactInfoToWikiCommentArtifact(artifactInfo);
        return this._wikiRestClient.addComment(request, wikiCommentArtifact.projectId, wikiCommentArtifact.wikiId, wikiCommentArtifact.pageId);
    }

    public updateComment(artifactInfo: ICommentArtifactInfo, commentId: number, value: string): Promise<Comment> {
        const wikiCommentArtifact = convertArtifactInfoToWikiCommentArtifact(artifactInfo);
        const updateRequest = { text: value } as CommentUpdateParameters;
        return this._wikiRestClient.updateComment(
            updateRequest,
            wikiCommentArtifact.projectId,
            wikiCommentArtifact.wikiId,
            wikiCommentArtifact.pageId,
            commentId
        );
    }

    public getComment(artifactInfo: ICommentArtifactInfo, commentId: number, includeDeleted?: boolean): Promise<Comment> {
        const wikiCommentArtifact = convertArtifactInfoToWikiCommentArtifact(artifactInfo);
        return this._wikiRestClient.getComment(
            wikiCommentArtifact.projectId,
            wikiCommentArtifact.wikiId,
            wikiCommentArtifact.pageId,
            commentId,
            includeDeleted
        );
    }

    public getComments(
        artifactInfo: ICommentArtifactInfo,
        count?: number,
        includeDeleted?: boolean,
        parentId?: number,
        continuationToken?: string,
        expand?: CommentExpandOptions
    ): Promise<ICommentPage> {
        const wikiCommentArtifact = convertArtifactInfoToWikiCommentArtifact(artifactInfo);
        return this._wikiRestClient
            .listComments(
                wikiCommentArtifact.projectId,
                wikiCommentArtifact.wikiId,
                wikiCommentArtifact.pageId,
                count,
                continuationToken,
                !includeDeleted,
                expand,
                undefined,
                parentId
            )
            .then(result => {
                return { comments: result.comments, continuationToken: result.continuationToken, totalCount: result.totalCount } as ICommentPage;
            });
    }

    public deleteComment(artifactInfo: ICommentArtifactInfo, commentId: number): Promise<void> {
        const wikiCommentArtifact = convertArtifactInfoToWikiCommentArtifact(artifactInfo);
        return this._wikiRestClient.deleteComment(wikiCommentArtifact.projectId, wikiCommentArtifact.wikiId, wikiCommentArtifact.pageId, commentId);
    }

    public addCommentReaction(artifactInfo: ICommentArtifactInfo, commentId: number, reactionType: CommentReactionType): Promise<CommentReaction> {
        const wikiCommentArtifact = convertArtifactInfoToWikiCommentArtifact(artifactInfo);
        return this._wikiRestClient.addCommentReaction(
            wikiCommentArtifact.projectId,
            wikiCommentArtifact.wikiId,
            wikiCommentArtifact.pageId,
            commentId,
            reactionType);
    }

    public deleteCommentReaction(artifactInfo: ICommentArtifactInfo, commentId: number, reactionType: CommentReactionType): Promise<CommentReaction> {
        const wikiCommentArtifact = convertArtifactInfoToWikiCommentArtifact(artifactInfo);
        return this._wikiRestClient.deleteCommentReaction(
            wikiCommentArtifact.projectId,
            wikiCommentArtifact.wikiId,
            wikiCommentArtifact.pageId,
            commentId,
            reactionType);
    }

    public getReactionRecentUsers(artifactInfo: ICommentArtifactInfo, commentId: number, reactionType: CommentReactionType): Promise<IdentityRef[]> {
        const wikiCommentArtifact = convertArtifactInfoToWikiCommentArtifact(artifactInfo);
        return this._wikiRestClient.getEngagedUsers(
            wikiCommentArtifact.projectId,
            wikiCommentArtifact.wikiId,
            wikiCommentArtifact.pageId,
            commentId,
            reactionType);
    }

    public addCommentAttachment(artifactInfo: ICommentArtifactInfo, content: File): Promise<CommentAttachment> {
        const wikiCommentArtifact = convertArtifactInfoToWikiCommentArtifact(artifactInfo);
        return this._wikiRestClient.createCommentAttachment(
            content,
            wikiCommentArtifact.projectId,
            wikiCommentArtifact.wikiId,
            wikiCommentArtifact.pageId
        );
    }
}
